import { curbZoneGeoJsonLayerModes, parkingLotMode } from "@/modules/curb-zone/curb-zone-map.const";
import { CurbZoneMapProps, CurbZonePageModeType } from "@/modules/curb-zone/curb-zone.type";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { CityContext } from "contexts/city-context-provider";
import { Feature, FeatureCollection, Polygon } from "geojson";
import { filterFeatureIndex } from "helper/map-utils";
import { EditableGeoJsonLayer, EditAction } from "@deck.gl-community/editable-layers";
import { useQuery } from "@apollo/client/react";
import { QUERY_CURB_ZONES_BY_PARKING_LOT } from "services/web/api-curb-zone.const";
import { CurbZonesResponse } from "services/web/api-curb-zone.type";
import { useQueryParkingLot } from "./use-query-parking-lot";
import { ParkingLot } from "services/web/api-parking-lot.type";
import { PickingInfo } from "deck.gl";
import { ParkingLotProperties } from "../components/parking-lot/modify-parking-lot-prompt";

type UseParkingLotParams = Pick<CurbZoneMapProps, "appMode">;
export type SavedParkingLotInput = {
  promptedProperties: ParkingLotProperties;
  curbZoneIds?: string[];
  geometry?: Polygon;
};

const toParkingLotFeature = (parkingLot: ParkingLot): Feature<Polygon, ParkingLot> => ({
  type: "Feature",
  id: parkingLot.id,
  geometry: parkingLot.geometry,
  properties: {
    id: parkingLot.id,
    name: parkingLot.name,
    address: parkingLot.address,
    note: parkingLot.note,
    curbZoneIds: parkingLot.curbZoneIds,
    cityId: parkingLot.cityId,
    geometry: parkingLot.geometry,
    position: parkingLot.position,
  },
});

export const useParkingLot = ({ appMode }: UseParkingLotParams) => {
  const { currentCity } = useContext(CityContext);
  const [openParkingLotModal, setOpenParkingLotModal] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const isDrawing = useRef(false);
  const drawnGeometry = useRef<Polygon>();
  const drawnCurbZoneIds = useRef<string[]>([]);
  const currentEditParkingLotLayer = useRef<Feature<Polygon, ParkingLot>>();
  const [parkingLotDetail, setParkingLotDetail] = useState<ParkingLot>();
  const [parkingLotEditionData, setParkingLotEditionData] = useState<FeatureCollection<Polygon, ParkingLot>>({
    type: "FeatureCollection",
    features: [],
  });

  const showParkingLotDetail = useMemo(
    () =>
      appMode === CurbZonePageModeType.EditParkingLot && parkingLotDetail && Object.keys(parkingLotDetail).length > 0,
    [appMode, parkingLotDetail],
  );

  const parkingLotIndexes = useMemo(
    () => filterFeatureIndex(parkingLotEditionData, parkingLotDetail),
    [parkingLotDetail, parkingLotEditionData],
  );

  const { refetch: refetchFilteredCurbZones, error: filteredCurbZoneError } = useQuery<CurbZonesResponse>(
    QUERY_CURB_ZONES_BY_PARKING_LOT,
    {
      variables: {
        filterInput: {
          cityId: currentCity?.id,
        },
      },
      skip: true,
    },
  );

  const { parkingLots, loading: parkingLotLoading, handleAddParkingLot, handleUpdateParkingLot } = useQueryParkingLot();

  useEffect(() => {
    if (filteredCurbZoneError) {
      resetEditParkingLotState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCurbZoneError]);

  useEffect(() => {
    if (!currentCity || parkingLotLoading) {
      return;
    }
    setParkingLotEditionData({
      type: "FeatureCollection",
      features: parkingLots.map(toParkingLotFeature),
    });
  }, [parkingLots, parkingLotLoading, currentCity]);

  const onEditParkingLotLayer = async ({
    updatedData,
    editType,
    editContext,
  }: EditAction<FeatureCollection<Polygon, ParkingLot>>) => {
    setParkingLotEditionData(updatedData);
    isDrawing.current = true;
    if (!["addFeature", "finishMovePosition"].includes(editType)) {
      return;
    }
    const newGeometry = updatedData.features[editContext.featureIndexes[0]].geometry;
    if (editType === "addFeature") {
      drawnGeometry.current = newGeometry;
    }
    const boundingBox = newGeometry.coordinates.flat(1);
    const { data } = await refetchFilteredCurbZones({
      filterInput: {
        cityId: currentCity?.id,
        boundingbox: boundingBox,
      },
    });
    if (!data?.curbZones.length) {
      if (appMode === CurbZonePageModeType.EditParkingLot) {
        setParkingLotEditionData({
          type: "FeatureCollection",
          features: parkingLots.map(toParkingLotFeature),
        });
      }
      setShowWarning(true);
      return;
    }
    const newIds = data.curbZones.map((item) => item.curbZoneId) as string[];
    if (appMode === CurbZonePageModeType.EditParkingLot) {
      await saveParkingLotProperties({
        promptedProperties: {
          name: parkingLotDetail?.name ?? "",
          address: parkingLotDetail?.address ?? "",
          note: parkingLotDetail?.note ?? "",
        },
        curbZoneIds: newIds,
        geometry: newGeometry,
      });
      if (parkingLotDetail) {
        setParkingLotDetail({ ...parkingLotDetail, geometry: newGeometry, curbZoneIds: newIds });
      }
    } else {
      drawnCurbZoneIds.current = newIds;
      setOpenParkingLotModal(true);
    }
  };

  const onClickParkingLotLayer = (info: PickingInfo) => {
    if (info.object && appMode === CurbZonePageModeType.EditParkingLot) {
      setParkingLotDetail({
        id: info.object.id,
        geometry: info.object.geometry,
        ...info.object.properties,
      });
      currentEditParkingLotLayer.current = info.object;
    }
  };

  const handleSelectParkingLot = (info: PickingInfo) => {
    if (info.layer?.id !== "edit-parking-lot-layer" || !info.object || !info.object?.id) {
      setParkingLotDetail(undefined);
      currentEditParkingLotLayer.current = Object.assign({});
      return;
    }
  };

  const resetEditParkingLotState = () => {
    if (appMode === CurbZonePageModeType.AddParkingLot) {
      setParkingLotEditionData((prev) => ({
        type: "FeatureCollection",
        features: prev.features.filter((feature) => feature.id),
      }));
    }
    isDrawing.current = false;
    drawnGeometry.current = undefined;
    drawnCurbZoneIds.current = [];
    setShowWarning(false);
    setParkingLotDetail(undefined);
    setOpenParkingLotModal(false);
    currentEditParkingLotLayer.current = Object.assign({});
  };

  const saveParkingLotProperties = async ({ promptedProperties, curbZoneIds, geometry }: SavedParkingLotInput) => {
    if (appMode === CurbZonePageModeType.AddParkingLot) {
      const newGeometry = geometry ?? drawnGeometry.current;
      if (!newGeometry) {
        throw new Error("Cannot create a parking lot without a drawn polygon");
      }
      await handleAddParkingLot({
        input: {
          ...promptedProperties,
          curbZoneIds: curbZoneIds ?? drawnCurbZoneIds.current,
          geometry: newGeometry,
          cityId: currentCity?.id ?? "",
        },
      });
      return;
    }

    if (!parkingLotDetail) {
      throw new Error("Cannot update a parking lot without a selected parking lot");
    }
    await handleUpdateParkingLot({
      input: {
        ...promptedProperties,
        curbZoneIds: curbZoneIds ?? parkingLotDetail.curbZoneIds,
        geometry: geometry ?? parkingLotDetail.geometry,
        cityId: currentCity?.id ?? "",
        id: parkingLotDetail.id,
      },
    });
  };

  const editableGeoJsonLayerMode = useMemo(() => {
    if (parkingLotMode.includes(appMode)) {
      return curbZoneGeoJsonLayerModes[appMode];
    }
    return curbZoneGeoJsonLayerModes[CurbZonePageModeType.View];
  }, [appMode]);

  const editableParkingLotLayer = new EditableGeoJsonLayer({
    id: "edit-parking-lot-layer",
    data: { type: parkingLotEditionData.type, features: parkingLotEditionData.features },
    mode: editableGeoJsonLayerMode,
    selectedFeatureIndexes: parkingLotIndexes,
    lineWidthMinPixels: 1,
    lineWidthMaxPixels: 1,
    autoHighlight: parkingLotMode.includes(appMode),
    getFillColor: [0, 0, 0, 0],
    getLineColor: [255, 0, 0, 255],
    onEdit: (updateData) => onEditParkingLotLayer(updateData as EditAction<FeatureCollection<Polygon, ParkingLot>>),
    onClick: onClickParkingLotLayer,
    updateTriggers: {
      mode: [appMode],
    },
  });

  const parkingLotLayers = [editableParkingLotLayer];

  return {
    parkingLotLayers,
    currentCity,
    parkingLotEditionData,
    openParkingLotModal,
    showParkingLotDetail,
    parkingLotDetail,
    setParkingLotDetail,
    setOpenParkingLotModal,
    showWarning,
    setShowWarning,
    resetEditParkingLotState,
    handleSelectParkingLot,
    saveParkingLotProperties,
  };
};
